import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import CancellationModal from "./CancellationModal";
import { Container, Button, Row, Col } from "reactstrap";
import {
  FaGolfBall,
  FaClock,
  FaCalendarAlt,
  FaDollarSign,
  FaMapMarkerAlt,
} from "react-icons/fa";
import styles from "../../styles/public/confirm-page.module.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const ConfirmPage = ({ bookingData, organization, showCancel = true, showExpectations = true }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const formatDate = (date) => {
    if (!date) return "Not selected";
    const dateObj = new Date(date);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleDateString("en-US", { month: "long" });
    return `${month} ${day}${getDaySuffix(day)}`;
  };

  const getDaySuffix = (day) => {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const handleCancel = () => {
    navigate("/cancel/" + bookingData.eventGuid);
  };

  const cost = bookingData.total;

  return (
    <Container className={`${styles["confirm-container"]} py-4`}>
      <div className="text-center mb-4">
        <h2 className={styles["confirm-title"]}>Your reservation is confirmed</h2>
      </div>

      <Row className="mb-4 pb-3 border-bottom">
        <Col md={8} className="mb-3 mb-md-0">
          <h3 className={styles["confirmation-details-title"]}>Reservation Details</h3>
          <div className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center gap-2">
              <FaGolfBall />
              <span>{bookingData.service?.title || "Custom"}</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <FaClock />
              <span>{formatDate(bookingData.date)}</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <FaCalendarAlt />
              <span>{bookingData.time || "Not set"}</span>
            </div>
            {cost && cost !== 0 && <div className="d-flex align-items-center gap-2">
              <FaDollarSign />
              <span>{parseFloat(cost).toFixed(2)} (due at check in)</span>
            </div>}
          </div>
        </Col>
        <Col md={4} className="d-flex justify-content-center align-items-center">
          <Container>
            {showCancel && <Row className="mb-3">
              <Button className={styles["cancel-button"]} color="secondary" onClick={handleCancel}>
                Cancel reservation
              </Button>
            </Row>}
            <Row>
              <Button color="link" className={styles["cancellation-link"]} onClick={toggleModal}>
                Cancellation policy
              </Button>
            </Row>
          </Container>
        </Col>
      </Row>

      <Row className="mb-4 pb-3 align-items-start">
        <Col md={6} className="mb-3 mb-md-0">
          <h3 className={styles["confirmation-details-title"]}>Location</h3>
          <p className="mb-0">
            <FaMapMarkerAlt className="me-1" /> {organization?.address || "Not set"}
          </p>
        </Col>
        <Col md={6}>
          <a href={organization?.map_click_url} target="_blank" rel="noopener noreferrer">
            <img src={organization?.map_url} alt="Map" className="responsive" style={{ width: '100%' }} />
          </a>
        </Col>
      </Row>

      {showExpectations && bookingData.service?.expectations && <>
        <div className="text-center mb-4 border-bottom">
          <h2 className={styles["confirm-title"]}>What to Expect</h2>
        </div>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{bookingData.service?.expectations}</ReactMarkdown>
      </>}


      <CancellationModal isOpen={isModalOpen} onClose={toggleModal} />
    </Container>
  );
};

export default ConfirmPage;